import { useEffect, useState } from 'react';
import { GoogleAuthProvider, signInWithCredential } from 'firebase/auth';
import { SIGNED_OUT_REASON_KEY, useAuth } from '../auth/AuthProvider';
import { auth, microsoftAuthEnabled } from '../firebase';
import { PRODUCT_NAME, PRODUCT_TAGLINE } from '../branding';
import logo from '../assets/compass-logo.png';

const devLogin = import.meta.env.DEV && !!import.meta.env.VITE_USE_EMULATORS;

function readSignedOutReason(): string | null {
  try {
    return sessionStorage.getItem(SIGNED_OUT_REASON_KEY);
  } catch {
    return null;
  }
}

export default function SignIn() {
  const { signInGoogle, signInMicrosoft } = useAuth();
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Read-only in the initializer (kept pure — safe under StrictMode's dev
  // double-invoke); the clear is a separate effect, since removing a key
  // twice is harmless but a lazy initializer that mutates isn't safe to
  // call twice.
  const [notice] = useState(() =>
    readSignedOutReason() === 'inactivity'
      ? 'You were signed out after 4 hours of inactivity. Sign in again to continue.'
      : null,
  );
  useEffect(() => {
    try {
      sessionStorage.removeItem(SIGNED_OUT_REASON_KEY);
    } catch {
      // ignore
    }
  }, []);

  async function run(fn: () => Promise<void>) {
    setErr(null);
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      const code = (e as { code?: string }).code ?? '';
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
        // user dismissed; no message needed
      } else {
        setErr((e as Error).message);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="center">
      <div className="signin">
        <img className="signin-logo" src={logo} alt={PRODUCT_NAME} />
        <h1>{PRODUCT_NAME}</h1>
        <p className="signin-tagline">{PRODUCT_TAGLINE}</p>
        {notice && <p className="signin-notice">{notice}</p>}
        <button className="btn primary" disabled={busy} onClick={() => run(signInGoogle)}>
          Sign in with Google
        </button>
        <button
          className="btn primary"
          disabled={busy || !microsoftAuthEnabled}
          title={microsoftAuthEnabled ? undefined : 'Microsoft sign-in is not configured yet'}
          onClick={() => run(signInMicrosoft)}
        >
          Sign in with Microsoft
          {!microsoftAuthEnabled && ' (coming soon)'}
        </button>
        {err && <p className="err">{err}</p>}

        {devLogin && (
          <>
            <hr style={{ margin: '16px 0', border: 0, borderTop: '1px solid var(--line)' }} />
            <p style={{ margin: 0 }}>Emulator dev sign-in</p>
            <button
              className="btn"
              disabled={busy}
              onClick={() =>
                run(async () => {
                  const cred = GoogleAuthProvider.credential(
                    JSON.stringify({
                      sub: 'dev-jason',
                      email: 'jasonrtyson@gmail.com',
                      email_verified: true,
                      name: 'Jason Tyson',
                    }),
                  );
                  await signInWithCredential(auth, cred);
                })
              }
            >
              Sign in as jasonrtyson@gmail.com
            </button>
          </>
        )}
      </div>
    </div>
  );
}
